import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { syncTokenToSW } from "@/lib/periodicSync";
import { getDeviceId, getDeviceName } from "@/lib/device";
import { useStudentSessionWS } from "@/lib/sessionWS";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  // Real-time WS — only active when a student is logged in
  useStudentSessionWS(!!user && user.role === "student");

  const hardLogout = (reason, extra = {}) => {
    localStorage.removeItem("pm_token");
    localStorage.removeItem("pm_user");
    syncTokenToSW(null).catch(() => {});
    setUser(null);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (reason === "session_replaced") {
      toast.error("Your account was logged in on another device. You have been logged out for security.", { duration: 8000 });
    } else if (reason === "account_disabled") {
      const word = (extra?.reason || "").toLowerCase();
      // deleted → "removed by an administrator"; inactive → "deactivated"; rejected → "was rejected"
      const msg = word === "deleted"
        ? "Your account has been removed by an administrator."
        : word === "inactive"
          ? "Your account has been deactivated by an administrator."
          : word === "rejected"
            ? "Your account request was rejected. Please contact support if you think this is a mistake."
            : "Your account is no longer active. Please contact support.";
      toast.error(msg, { duration: 10000 });
    }
    // Navigate to /login (unless already there)
    if (typeof window !== "undefined" && !window.location.pathname.includes("/login")) {
      window.location.replace("/login");
    }
  };

  useEffect(() => {
    const onAuthEvent = (e) => {
      const kind = e?.detail?.kind;
      if (kind === "session_replaced") hardLogout("session_replaced");
      else if (kind === "account_disabled") hardLogout("account_disabled", e.detail || {});
      else if (kind === "logged_out") hardLogout("logged_out");
    };
    window.addEventListener("pm-auth-event", onAuthEvent);
    return () => window.removeEventListener("pm-auth-event", onAuthEvent);
  }, []);

  // Polling: every 30s validate that this device's session is still the active one
  const startPolling = (role) => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (role !== "student") return;  // only students have the one-device rule
    pollRef.current = setInterval(() => {
      api.get("/auth/session/check").catch(() => { /* interceptor handles 401 */ });
    }, 30000);
  };

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth callback, skip the /auth/me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("pm_token");
    const cached = localStorage.getItem("pm_user");
    if (token && cached) {
      let parsed = null;
      try { parsed = JSON.parse(cached); setUser(parsed); } catch (e) { /* noop */ }
      syncTokenToSW(token).catch(() => {});
      api.get("/auth/me").then((r) => {
        setUser(r.data);
        localStorage.setItem("pm_user", JSON.stringify(r.data));
        startPolling(r.data.role);
      }).catch(() => { /* interceptor already cleared on 401 */ }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const login = async (email, password, turnstileToken) => {
    const r = await api.post("/auth/login", {
      email, password,
      device_id: getDeviceId(),
      device_name: getDeviceName(),
      turnstile_token: turnstileToken || null,
    });
    localStorage.setItem("pm_token", r.data.token);
    localStorage.setItem("pm_user", JSON.stringify(r.data.user));
    syncTokenToSW(r.data.token).catch(() => {});
    setUser(r.data.user);
    startPolling(r.data.user.role);
    if (r.data.replaced_previous_device) {
      toast.success("You are now logged in on this device. Any previous device has been logged out.", { duration: 5000 });
    }
    return r.data.user;
  };

  const logout = () => {
    // Best-effort: tell backend to clear the device session so old token can't be reused.
    api.post("/auth/logout").catch(() => {});
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    localStorage.removeItem("pm_token");
    localStorage.removeItem("pm_user");
    syncTokenToSW(null).catch(() => {});
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
